import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import useMobileMediaQuery from '@/hooks/useMobileMediaQuery'
import { usePdfDownload } from '@/hooks/usePdfDownload'
import useSmallMobileMediaQuery from '@/hooks/useSmallMobileMediaQuery'
import useTabletMediaQuery from '@/hooks/useTabletMediaQuery'
import type {
  SubjectInputs,
  ReportCheckData,
} from '@/store/api/Account/Account.types'
import {
  EReportType,
  ESocialCheckStatus,
} from '@/store/api/Enterprise/Enterprise.types'
import {
  useMakeBackgroundCheckOpenedMutation,
  useReDowloadPdfMutation,
} from '@/store/api/Person/Person.query'
import { formatDate } from '@/utils/date'
import { textEllipsis } from '@/utils/strings'

import { Chip } from '../../../components/Chip/Chip'

import { BurgerMenu } from './BurgerMenu'

export const SocialAnalysisItem = (data: ReportCheckData) => {
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()
  const isSmallMobile = useSmallMobileMediaQuery()
  const isTablet = useTabletMediaQuery()
  const isMobileDevice = isTablet || isSmallMobile || isMobile || isSmallMobile

  const navigate = useNavigate()

  const [reDowloadPDF, { isLoading: isRedownloading }] =
    useReDowloadPdfMutation()
  const [makeBackgroundCheckOpened] = useMakeBackgroundCheckOpenedMutation()
  const { downloadPdf, isDownloading } = usePdfDownload({
    reportId: data.subjectId,
  })

  const isLoading = isRedownloading || isDownloading

  const extractInputInitials = (input: SubjectInputs): string => {
    const fullname = input.name + input.lastName
    const words = fullname.split(' ')
    return (
      words?.[0]?.slice(0, 1) +
      ' ' +
      words?.[words?.length - 1]?.slice(0, 1)
    )?.toUpperCase()
  }

  const initials = useMemo(() => {
    if (!data.inputs.name) {
      return ''
    }
    const fullname = data.inputs.name
    const initials = fullname.split(' ')
    const firstInitial = initials[0] ? initials[0][0].toUpperCase() : ''
    const secondInitial = initials[2] ? initials[2][0].toUpperCase() : ''
    return `${firstInitial} ${secondInitial}`
  }, [data.inputs.name])

  const onPressDowload = async () => {
    if (data.pdf) {
      // Use the new endpoint to get a signed URL
      await downloadPdf()
    } else if (data.status === 'PendingDownloadPdf') {
      try {
        const res = await reDowloadPDF({ id: data._id }).unwrap()
        if (res.info.url) {
          window.open(res.info.url, '_blank')
        }
      } catch (e) {
        catchWithSentry(e)
        console.error(e)
      }
    }
    try {
      await makeBackgroundCheckOpened({ id: data._id }).unwrap()
    } catch (e) {
      catchWithSentry(e)
      console.error(e)
    }
  }

  const isPending =
    data.status !== ESocialCheckStatus.Complete &&
    data.status !== ESocialCheckStatus.NotReleased

  const isCancelled = data.status === ESocialCheckStatus.Cancelled

  const isImpersonation = data.reportType === EReportType.ImpersonationCheck

  const totalFlags = useMemo(() => {
    if (!data.risks || !Array.isArray(data.risks)) {
      return 0
    }
    return data.risks.reduce((total, risk) => total + risk.count, 0)
  }, [data.risks])

  return (
    <div className="bg-baseColor rounded-2xl flex flex-row justify-normal p-3 h-[65px] w-full max-sm:p-1.5">
      <div className="flex w-1/2 max-lg:justify-between max-lg:w-full ">
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage />
            <AvatarFallback className="bg-baseColor text-neutralColor">
              {extractInputInitials(data.inputs)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <div className="flex items-center gap-2 max-md:flex-col max-sm:w-min">
              <p className="text-neutralColor text-base font-medium text-nowrap">
                {textEllipsis(
                  data.inputs.name + ' ' + data.inputs.lastName,
                  isMobile ? 14 : 50,
                )}
              </p>

              {isImpersonation && !isMobileDevice && (
                <Chip
                  variant="primaryBlue"
                  size="tag"
                  text={t('dashboard.reports.impersonationType')}
                />
              )}

              {isPending && !isCancelled && !isMobileDevice && (
                <Chip
                  variant="blue"
                  size="tag"
                  text={t('dashboard.reports.inProgress')}
                />
              )}

              {!data.pdfViewed &&
                !isPending &&
                !isCancelled &&
                !isMobileDevice && (
                  <Chip
                    variant="green"
                    size="tag"
                    text={t('dashboard.reports.new')}
                  />
                )}

              {isCancelled && !isMobileDevice && (
                <Chip
                  variant="red"
                  size="tag"
                  text={t('dashboard.reports.cancelled')}
                />
              )}
            </div>
            <p className="text-neutralColor60 text-sm font-medium tracking-tight">
              {data.inputs.email &&
                textEllipsis(data.inputs.email, isMobile ? 13 : 50)}
            </p>
          </div>
        </div>

        {isPending && !isCancelled && isMobileDevice && (
          <Chip
            variant="blue"
            size="tag"
            text={t('dashboard.reports.pending')}
          />
        )}

        {!data.pdfViewed && !isPending && !isCancelled && isMobileDevice && (
          <Chip variant="green" size="tag" text={t('dashboard.reports.new')} />
        )}

        {isCancelled && isMobileDevice && (
          <Chip
            variant="red"
            size="tag"
            text={t('dashboard.reports.cancelled')}
          />
        )}
      </div>
      <div className="flex lg:w-1/3 items-center max-sm:flex-col">
        {!isMobileDevice && (
          <div className="flex flex-col w-full gap-1">
            <p className="text-neutralColor text-xs font-medium">
              {t('dashboard.reports.createdAt')}
            </p>
            <p className="text-neutralColor60 text-xs font-medium ">
              {formatDate(data.createdAt, 'MM/dd/yyyy')}
            </p>
          </div>
        )}

        {!isMobileDevice &&
          !isImpersonation &&
          data.status === ESocialCheckStatus.Complete && (
            <div className="mr-2 flex flex-col gap-1 w-min">
              <Chip
                text={`${totalFlags} flags found`}
                size="tag"
                variant={totalFlags === 0 ? 'green' : 'red'}
              />
            </div>
          )}
      </div>

      {isMobileDevice && (
        <div className="flex justify-end gap-2">
          {isImpersonation && data.status === ESocialCheckStatus.Complete && (
            <Button
              variant="secondary"
              onClick={() => {
                navigate(AppRoutes.impersonationReport, {
                  state: { id: data._id },
                })
              }}
              className="rounded-full"
            >
              {t('dashboard.reports.view')}
            </Button>
          )}

          {!isPending && data.pdf && (
            <BurgerMenu
              data={data}
              isLoading={isLoading}
              onPressDownload={onPressDowload}
              name={data.inputs.name}
              initials={initials}
            />
          )}
        </div>
      )}

      {!isMobileDevice && (
        <div className="flex lg:w-1/3 justify-end items-start gap-2">
          {data.status === ESocialCheckStatus.Complete && (
            <Button
              variant="secondary"
              onClick={() => {
                if (isImpersonation) {
                  navigate(AppRoutes.impersonationReport, {
                    state: { id: data._id },
                  })
                } else {
                  navigate(AppRoutes.digitalReport, {
                    state: { id: data._id, subjectId: data.subjectId },
                  })
                }
              }}
              className="rounded-full"
            >
              {t('dashboard.reports.view')}
            </Button>
          )}

          {/* Impersonation reports have no PDF */}
          {!isImpersonation && (
            <Button
              variant="default"
              isLoading={isLoading}
              disabled={!data.pdf}
              onClick={() => {
                onPressDowload()
              }}
              className="rounded-full"
            >
              {t('dashboard.reports.download')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
